import { useUser } from '@clerk/clerk-react';
import { Heart, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  TERRITORIES_LIKES_API_URL,
  TERRITORIES_REQUESTS_API_URL,
} from '@/constants/apis';
import { configuration } from '@/constants/config';
import { TERRITORIES_TOUR_CLASSES } from '@/constants/tour/classNames';
import { apiManager } from '@/helpers/api.manager';
import { toast } from '@/hooks/ui/use-toast';
import { useTerritoryStore } from '@/stores/useTerritoryStore';
import { Territory } from '@/types/territories/territory.type';

import { DemographicSection } from './DemographicSection';
import { TerritoryReserveDialog } from './TerritoryReserveDialog';

interface TerritoryDetailViewProps {
  territory: Territory;
}

const TerritoryDetailView = ({ territory }: TerritoryDetailViewProps) => {
  const { user } = useUser();
  const {
    territories,
    mapTerritories,
    likedTerritories,
    setTerritories,
    setMapTerritories,
    setLikedTerritories,
  } = useTerritoryStore();

  const [confirmReserveDialogOpen, setConfirmReserveDialogOpen] =
    useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRequestReserveLoading, setIsRequestReserveLoading] =
    useState<boolean>(false);

  const handleLikeTerritory = async (): Promise<void> => {
    setIsLoading(true);
    if (territory.is_liked) {
      const response = await apiManager.delete(
        `${TERRITORIES_LIKES_API_URL}/${territory.id}`,
        {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setIsLoading(false);
      if (response.status !== 200) {
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
        });
        return;
      }

      setTerritories(
        territories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: false, likes_count: t.likes_count - 1 }
            : t
        ) || []
      );

      setMapTerritories(
        mapTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: false, likes_count: t.likes_count - 1 }
            : t
        ) || []
      );
      setLikedTerritories(
        likedTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: false, likes_count: t.likes_count - 1 }
            : t
        ) || []
      );

      toast({
        description: 'Territory unliked successfully',
      });
    } else {
      const response = await apiManager.post(
        `${TERRITORIES_LIKES_API_URL}/${territory.id}`,
        undefined,
        {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setIsLoading(false);
      if (response.status !== 200) {
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
        });
        return;
      }

      setTerritories(
        territories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: true, likes_count: t.likes_count + 1 }
            : t
        ) || []
      );

      setMapTerritories(
        mapTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: true, likes_count: t.likes_count + 1 }
            : t
        ) || []
      );
      setLikedTerritories(
        likedTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, is_liked: true, likes_count: t.likes_count + 1 }
            : t
        ) || []
      );

      toast({
        description: 'Territory liked successfully',
      });
    }
  };

  const handleRequestReserve = async (): Promise<void> => {
    setIsRequestReserveLoading(true);
    if (territory.request_status) {
      const response = await apiManager.delete(
        `${TERRITORIES_REQUESTS_API_URL}/${territory.id}`,
        {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setIsRequestReserveLoading(false);
      if (response.status !== 200) {
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
        });
        return;
      }

      setTerritories(
        territories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: null, status: 'AVAILABLE' }
            : t
        ) || []
      );

      setMapTerritories(
        mapTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: null, status: 'AVAILABLE' }
            : t
        ) || []
      );
      setLikedTerritories(
        likedTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: null, status: 'AVAILABLE' }
            : t
        ) || []
      );

      toast({
        description: 'Request canceled successfully',
      });
    } else {
      const response = await apiManager.post(
        `${TERRITORIES_REQUESTS_API_URL}/${territory.id}`,
        undefined,
        {
          baseURL: configuration.TERRITORIES_API_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setIsRequestReserveLoading(false);
      if (response.status !== 200) {
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
        });
        return;
      }

      setTerritories(
        territories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: 'REQUESTED', status: 'PENDING' }
            : t
        ) || []
      );

      setMapTerritories(
        mapTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: 'REQUESTED', status: 'PENDING' }
            : t
        ) || []
      );
      setLikedTerritories(
        likedTerritories?.map((t) =>
          t.id === territory.id
            ? { ...t, request_status: 'REQUESTED', status: 'PENDING' }
            : t
        ) || []
      );

      toast({
        description: 'Request sent successfully',
      });
    }
    setConfirmReserveDialogOpen(false);
  };

  return (
    <div className="bg-muted p-4">
      <div className="rounded-lg border border-border bg-white p-4">
        <div className="flex gap-4 max-md:flex-col">
          <div
            className={`${TERRITORIES_TOUR_CLASSES.TERRITORY_DEMOGRAPHIC_SECTION} flex-1 max-md:order-2`}
          >
            <DemographicSection territory={territory} />
          </div>
          <div className="flex gap-2 max-md:order-1">
            <Button
              size="sm"
              variant="outline"
              disabled={isLoading}
              onClick={handleLikeTerritory}
              className={TERRITORIES_TOUR_CLASSES.TERRITORY_LIKE_BUTTON}
            >
              {isLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Heart
                  fill={territory.is_liked ? 'currentColor' : 'none'}
                  className="size-4 text-red-500"
                  stroke="black"
                />
              )}
              {territory.is_liked ? <>Liked</> : <>Like</>}
            </Button>
            {territory.status === 'AVAILABLE' ? (
              <TerritoryReserveDialog
                isOpen={confirmReserveDialogOpen}
                onOpenChange={setConfirmReserveDialogOpen}
                onConfirm={handleRequestReserve}
                loading={isRequestReserveLoading}
              />
            ) : territory.status === 'PENDING' &&
              territory.requested_by?.id === user?.id ? (
              <Button
                size="sm"
                variant="outline"
                disabled={isRequestReserveLoading}
                onClick={handleRequestReserve}
                className={TERRITORIES_TOUR_CLASSES.TERRITORY_RESERVE_BUTTON}
              >
                {isRequestReserveLoading && (
                  <Loader2 className="size-4 animate-spin" />
                )}
                Requested
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export { TerritoryDetailView };
